import React from "react";
import { Plus, Loader2, Copy, CheckCircle, Eye, EyeOff, Trash2 } from "lucide-react";
import { FcCancel } from "react-icons/fc";

const ApiKeyManagement = ({
  apiKeys = [],
  loading = false,
  visibleKeys = {},
  copyStatus = {},
  toggleKeyVisibility,
  copyToClipboard,
  handleRevokeApiKey,
  handleDeleteApiKey,
  onOpenCreateModal,
}) => {
  const getStatusColor = (isActive) => {
    if (isActive === true) return "bg-green-800 text-white";
    if (isActive === false) return "bg-red-600 text-white";
    return "bg-gray-100 text-gray-800";
  };

  const getStatusText = (isActive) => {
    if (isActive === true) return "active";
    if (isActive === false) return "revoked";
    return "inactive";
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">API Keys</h3>
          <p className="text-sm text-gray-500">
            Manage your API keys for authenticating request calls.
          </p>
        </div>
        <button
          onClick={onOpenCreateModal}
          className="bg-secondary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create New Key
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-secondary" />
          <span className="ml-2 text-gray-600">Loading API keys...</span>
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          No API keys found. Click "Create New Key" to generate one.
        </div>
      ) : (
        <div className="space-y-4">
          {apiKeys.map((key) => (
            <div
              key={key._id}
              className="bg-[#F4F8F7] rounded-lg border border-gray-200 p-4"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-0">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-medium text-gray-900">{key.name}</h4>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        key.isActive
                      )}`}
                    >
                      {getStatusText(key.isActive)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <code className="text-sm text-gray-600 font-mono break-all">
                      {visibleKeys[key._id]
                        ? key.key
                        : "••••••••••••••••••••••••••••••••"}
                    </code>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => copyToClipboard(key.key, key._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 relative"
                        title="Copy API Key"
                      >
                        {copyStatus[key._id] ? (
                          <span className="flex items-center gap-1 text-green-500 text-xs font-medium">
                            <CheckCircle className="w-4 h-4" /> Copied
                          </span>
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => toggleKeyVisibility(key._id)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title={visibleKeys[key._id] ? "Hide API Key" : "Show API Key"}
                      >
                        {visibleKeys[key._id] ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Created: {new Date(key.createdAt).toLocaleDateString()}
                    {key.lastUsed ? ` • Last used: ${new Date(key.lastUsed).toLocaleDateString()}` : ""}
                  </div>
                  {key.description && (
                    <div className="text-xs text-gray-600 mt-1">
                      {key.description}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {key.isActive && (
                    <button
                      onClick={() => handleRevokeApiKey(key._id)}
                      className="p-2 text-red-500 hover:text-red-700"
                      title="Revoke API Key"
                    >
                      <FcCancel className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteApiKey(key._id)}
                    className="p-2 text-red-500 hover:text-red-700"
                    title="Delete API Key"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApiKeyManagement;
